/**
 * Moderation Settings Page
 * Configure content moderation and safety settings for children
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield, AlertTriangle, Bell, Eye, Plus, X, Save } from 'lucide-react';
import { getChildren, getSettings, updateSettings } from '../services/childService';
import { getErrorMessage } from '../services/api';
import type { ChildProfileWithConsent, ParentSettings } from '@kids-lms/types';

export default function ModerationPage() {
  const [children, setChildren] = useState<ChildProfileWithConsent[]>([]);
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [settings, setSettings] = useState<ParentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newKeyword, setNewKeyword] = useState('');

  useEffect(() => {
    loadChildren();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadSettings(selectedChild);
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const data = await getChildren();
      setChildren(data);
      if (data.length > 0) {
        setSelectedChild(data[0].id);
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async (childId: string) => {
    try {
      setLoading(true);
      const data = await getSettings(childId);
      setSettings(data);
    } catch {
      // Settings might not exist yet, use defaults
      setSettings({
        id: '',
        childId,
        allowedTopics: [],
        blockedKeywords: [],
        notificationPreferences: {
          dailySummary: true,
          weeklyReport: true,
          flaggedContent: true,
          sessionStart: false,
          timeLimit: true,
        },
        pushTokenIos: null,
        pushTokenAndroid: null,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedChild || !settings) return;

    try {
      setSaving(true);
      setError('');
      await updateSettings(selectedChild, {
        allowedTopics: settings.allowedTopics,
        blockedKeywords: settings.blockedKeywords,
        notificationPreferences: settings.notificationPreferences,
      });
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addBlockedKeyword = () => {
    if (newKeyword.trim() && settings) {
      setSettings({
        ...settings,
        blockedKeywords: [...(settings.blockedKeywords || []), newKeyword.trim()],
      });
      setNewKeyword('');
    }
  };

  const removeBlockedKeyword = (keyword: string) => {
    if (settings) {
      setSettings({
        ...settings,
        blockedKeywords: (settings.blockedKeywords || []).filter(k => k !== keyword),
      });
    }
  };

  const toggleNotification = (key: keyof NonNullable<ParentSettings['notificationPreferences']>) => {
    if (settings && settings.notificationPreferences) {
      setSettings({
        ...settings,
        notificationPreferences: {
          ...settings.notificationPreferences,
          [key]: !settings.notificationPreferences[key],
        },
      });
    }
  };

  if (loading && children.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kid-ocean border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/parent"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-kid-violet/10 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-kid-violet" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Moderation Settings</h1>
            <p className="text-gray-600 dark:text-gray-400">Configure safety and content filters</p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 rounded-xl">
            {success}
          </div>
        )}

        {children.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Children Added</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">Add a child profile first to configure moderation settings.</p>
            <Link
              to="/parent/add-child"
              className="inline-flex items-center gap-2 px-4 py-2 bg-kid-ocean text-white rounded-lg hover:bg-kid-ocean/90"
            >
              <Plus className="w-4 h-4" />
              Add Child
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Child Selector */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Child
              </label>
              <select
                value={selectedChild || ''}
                onChange={(e) => setSelectedChild(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-kid-ocean"
              >
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Content Filtering */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-kid-violet" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Content Filtering</h2>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Custom Blocked Keywords
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Add words or phrases that should be blocked in conversations.
                  </p>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBlockedKeyword())}
                      placeholder="Add keyword..."
                      className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={addBlockedKeyword}
                      className="px-4 py-2 bg-kid-violet text-white rounded-lg hover:bg-kid-violet/90"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(settings?.blockedKeywords || []).map((keyword) => (
                      <span
                        key={keyword}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm"
                      >
                        {keyword}
                        <button
                          onClick={() => removeBlockedKeyword(keyword)}
                          className="hover:text-red-900 dark:hover:text-red-300 p-2 touch-slop rounded-full"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-kid-tangerine" />
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h2>
              </div>

              <div className="space-y-4">
                {[
                  { key: 'dailySummary' as const, label: 'Daily Summary', description: 'Receive a daily summary of your child\'s activity' },
                  { key: 'weeklyReport' as const, label: 'Weekly Report', description: 'Receive detailed weekly learning reports' },
                  { key: 'flaggedContent' as const, label: 'Flagged Content Alerts', description: 'Get notified when content is flagged by moderation' },
                  { key: 'sessionStart' as const, label: 'Session Start', description: 'Get notified when your child starts a learning session' },
                  { key: 'timeLimit' as const, label: 'Time Limit Alerts', description: 'Get notified when daily time limit is reached' },
                ].map(({ key, label, description }) => (
                  <label key={key} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings?.notificationPreferences?.[key] ?? false}
                      onChange={() => toggleNotification(key)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-kid-ocean focus:ring-kid-ocean"
                    />
                    <div>
                      <span className="font-medium text-gray-900 dark:text-white">{label}</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-6 py-3 bg-kid-ocean text-white rounded-xl hover:bg-kid-ocean/90 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
